import gsap from "gsap";

// Unlocked achievements are saved to localStorage, so that you need to unlock them every time you open the page anew
const SESSION_IDENTIFIER = "achievements";
const SESSION_SEPARATOR = " ";

const ACHIEVEMENTS_ENABLED_IDENTIFIER = "achievements_enabled";

window.AchievementsEnabled = null;
setAchievementsEnabled(areAchievementsEnabled());

/**
 * Initialize the achievements.
 *
 * @returns void
 */
export default function InitAchievements() {
	// Check if the necessary DOM elements for achievements are present
	const achievementElements = document.querySelectorAll(".achievements .achievement");
	if (achievementElements.length === 0) return;

	// Create the achievements array if it doesn't already exist
	if (!window.Achievements && !Array.isArray(window.Achievements)) window.Achievements = [];

	// Create achievement for every achievement element in DOM
	achievementElements.forEach((achievementElement) => {
		window.Achievements[new String(achievementElement.dataset.slug).trim()] = new Achievement(achievementElement);
	});
}
class Achievement {
	constructor(element) {
		this.element = element;
		this.name = this.element.querySelector('.achievement-name').textContent ?? '';
		this.slug = new String(element.dataset.slug).trim();
		this.achieved = this._beenAchieved();
		if (this.achieved) this._payload();
	}

	/**
	 * Achieves the achievement.
	 *
	 * That makes it show the slide-in, set it in the session variable and run the achievements payload.
	 */
	achieve() {
		if (this.achieved) return;

		this.achieved = true;

		if (areAchievementsEnabled()) this._animate();

		let settingsString = localStorage.getItem(SESSION_IDENTIFIER);
		let settings = !settingsString ? [] : atob(settingsString).split(SESSION_SEPARATOR);

		if (settings.indexOf(this.slug) === -1) settings.push(this.slug);

		localStorage.setItem(SESSION_IDENTIFIER, btoa(settings.join(SESSION_SEPARATOR)));

		this._payload();

		if (areAchievementsEnabled() && window.audioElements && window.audioElements.tada)
			window.audioElements.tada[Math.floor(Math.random() * window.audioElements.tada.length)].play();
	}

	/**
	 * Animate the slide-in.
	 */
	_animate() {
		this.timeline = gsap.timeline();
		this.timeline.fromTo(
			this.element,
			{
				translateY: "-200%",
			},
			{
				translateY: "0%",
			}
		);
		this.timeline.fromTo(
			this.element,
			{
				translateY: "0%",
			},
			{
				translateY: "-200%",
			},
			"+=5"
		);
		this.timeline.play();
	}

	/**
	 * Run the payload.
	 */
	_payload() {
		let currentAchievement = document.querySelector('.current-achievement');
		if (currentAchievement) {
			currentAchievement.classList.remove('hidden');
			currentAchievement.querySelector('span:last-child').textContent = this.name;
		}

		// yes, this code is terrible, but it does the job fine for now
		if (this.slug === "sound") soundAchievementPayload();
		if (this.slug === "ghost") ghostAchievementPayload();
	}

	/**
	 * Returns if the achievement has been achieved.
	 *
	 * @returns bool wheter the achievement has been achieved
	 */
	_beenAchieved() {
		let settingsString = localStorage.getItem(SESSION_IDENTIFIER);
		let settings = !settingsString ? [] : atob(settingsString).split(SESSION_SEPARATOR);

		return settings.indexOf(this.slug) > -1;
	}
}

function soundAchievementPayload() {
	// Get all the event targets
	const dryvenLogos = document.querySelectorAll(".headermenu > .logo img");
	const swooshAnchorlinks = document.querySelectorAll('a[href^="#"], a[href^="/#"], .card.service');
	const clickCards = document.querySelectorAll('.megamenu .mm-item > a:not([href^="/#"])');

	// Object with sound variant number for each sound effect
	window.audioElements = {
		click: 2,
		dryven: 4,
		swoosh: 2,
		tada: 3,
	};

	// Create a audio object for each of these and save them to the audioElements object
	Object.keys(audioElements).map((key, index) => {
		audioElements[key] = [...Array(audioElements[key]).keys()].map((soundNumber) => {
			return new Audio(`/audio/${key}${soundNumber + 1}.mp3`);
		});
	});

	/**
	 * Adds a listener for sound effects on event target pools.
	 *
	 * @param {NodeList} elementPool event targets
	 * @param {string} eventType the event type to look out for
	 * @param {Array} audioElementsArray the array to randomly choose an audio from
	 */
	function addSoundListener(elementPool, eventType, audioElementsArray) {
		if (elementPool.length > 0) {
			const playRandomSound = () => {
				if (!window.AchievementsEnabled) return;
				audioElementsArray[Math.floor(Math.random() * audioElementsArray.length)].play();
			};

			elementPool.forEach((element) => element.addEventListener(eventType, playRandomSound));
		}
	}

	// Add sound effects to each event target pool
	addSoundListener(dryvenLogos, "mouseenter", window.audioElements.dryven);
	addSoundListener(swooshAnchorlinks, "click", window.audioElements.swoosh);
	addSoundListener(clickCards, "click", window.audioElements.click);
}

function ghostAchievementPayload() {
	const ghostImageElement = document.querySelector(".achievements .pixelated-ghost");
	if (!ghostImageElement) return;

	window.ghostYoyo = gsap
		.to(ghostImageElement, {
			translateY: "4rem",
			yoyo: true,
			repeat: Number.MAX_SAFE_INTEGER,
			ease: "sine.inOut",
		})
		.paused(!areAchievementsEnabled());

	window.ghostTimeline = gsap
		.timeline({
			repeat: Number.MAX_SAFE_INTEGER,
		})
		.to(ghostImageElement, { scaleX: 1 })
		.fromTo(
			ghostImageElement,
			{
				translateX: "-4vw",
			},
			{
				translateX: "100vw",
				duration: 12.5,
			}
		)
		.to(ghostImageElement, { scaleX: -1 })
		.fromTo(
			ghostImageElement,
			{
				translateX: "102vw",
			},
			{
				translateX: "-4vw",
				duration: 12.5,
			}
		)
		.paused(!areAchievementsEnabled());

	ghostImageElement.addEventListener("mouseenter", () => {
		gsap.to(ghostImageElement, {
			autoAlpha: 0.0,
			duration: 0.2,
		});

		setTimeout(() => {
			if (!areAchievementsEnabled()) return;

			gsap.to(ghostImageElement, {
				autoAlpha: 1.0,
				duration: 0.75,
			});
		}, 10000);
	});
}

/**
 * Set if achievements should be enabled and call events accordingly.
 *
 * @param {bool} value whether achievements should be enabled
 */
export function setAchievementsEnabled(value) {
	localStorage.setItem(ACHIEVEMENTS_ENABLED_IDENTIFIER, value);
	window.AchievementsEnabled = value;

	const heartTimer = document.querySelector(".heart-timer");
	if (!heartTimer) return;

	const toggleSwitch = heartTimer.querySelector(".switch input");
	const pixelatedGhost = document.querySelector(".achievements img.pixelated-ghost");

	if (heartTimer) {
		if (value) heartTimer.classList.remove("disabled");
		else heartTimer.classList.add("disabled");
	}

	// Set toggle switch
	if (toggleSwitch) toggleSwitch.checked = value;

	// Show/hide flying ghost
	if (pixelatedGhost) {
		if (value) {
			gsap.to(pixelatedGhost, {
				autoAlpha: 1.0,
				duration: 0.75,
			});

			if (window.ghostTimeline) window.ghostTimeline.resume();
			if (window.ghostYoyo) window.ghostYoyo.resume();
		} else {
			gsap.to(pixelatedGhost, {
				autoAlpha: 0.0,
				duration: 0.2,
			});

			if (window.ghostTimeline) window.ghostTimeline.pause();
			if (window.ghostYoyo) window.ghostYoyo.pause();
		}
	}
}

export function areAchievementsEnabled() {
	// Return true if it's not set or is exactly true
	return localStorage.getItem(ACHIEVEMENTS_ENABLED_IDENTIFIER) === null || localStorage.getItem(ACHIEVEMENTS_ENABLED_IDENTIFIER) === "true";
}
